import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import { CatalogFormFieldEntity } from 'entities/CatalogFormField';
import { FieldsCatalogEntity, normalizeFieldsCatalogData } from 'entities/Form';
import { FieldsCatalogEntityApi } from 'entities/endpoints/form.fields_list';
import { ApiRequest } from 'stores/common';

type PrivateFields = '_commonFields' | '_myFields' | '_linearizeFields';

export default class CatalogFieldsStore {
  constructor() {
    makeObservable<CatalogFieldsStore, PrivateFields>(this, {
      _myFields: observable.ref,
      _commonFields: observable.ref,

      _linearizeFields: computed,
      fieldsCollection: computed,
      requiredFields: computed,
      requiredFieldsCount: computed,
      data: computed,

      fetchData: action.bound,
    });
  }

  /**
   * Общие поля
   * @private
   */
  private _commonFields: Record<string, CatalogFormFieldEntity[]> = {};

  /**
   * Пользовательские поля
   * @private
   */
  private _myFields: CatalogFormFieldEntity[] = [];

  readonly fetchFormDataRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.fieldsList,
    methodType: 'GET',
  });

  /**
   * Массив всех полей
   * @private
   */
  get _linearizeFields(): CatalogFormFieldEntity[] {
    return [...Object.values(this._commonFields), this._myFields].reduce(
      (acc, curr) => {
        return acc.concat(curr);
      },
      [] as CatalogFormFieldEntity[]
    );
  }

  /**
   * Коллекция всех полей (для join'а с полями таблицы)
   */
  get fieldsCollection(): Record<number, CatalogFormFieldEntity> {
    return this._linearizeFields.reduce(
      (accum, elem) => ({
        ...accum,
        [elem.id]: elem,
      }),
      {} as Record<number, CatalogFormFieldEntity>
    );
  }

  get requiredFields(): Record<string, CatalogFormFieldEntity[]> {
    const result: Record<string, CatalogFormFieldEntity[]> = {};

    Object.entries(this._commonFields).forEach(([groupName, groupFields]) => {
      const requiredGroupFields = groupFields.filter((field) => field.required);

      if (requiredGroupFields.length > 0) {
        result[groupName] = requiredGroupFields;
      }
    });

    return result;
  }

  get requiredFieldsCount(): number {
    return Object.values(this.requiredFields).reduce(
      (sum, group) => sum + group.length,
      0
    );
  }

  /** Инфа по полям */
  get data(): FieldsCatalogEntity<CatalogFormFieldEntity> {
    return {
      commonFields: this._commonFields,
      myFields: this._myFields,
    };
  }

  /**
   * Загрузка данных каталога
   */
  async fetchData(): Promise<void> {
    const { data, isError } =
      await this.fetchFormDataRequest.call<FieldsCatalogEntityApi>();

    if (!isError && data) {
      runInAction(() => {
        const { commonFields, myFields } = normalizeFieldsCatalogData(
          data,
          (e) => e
        );

        this._myFields = myFields;
        this._commonFields = commonFields;
      });
    }
  }
}
